import React, { useState, useEffect, useMemo, useRef } from 'react';
import FlippexLoader from '../components/ui/FlippexLoader';
import axios from 'axios';
import { useSearchParams, useNavigationType } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Spinner, ArrowRight } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { OpportunityCard } from '../components/marketplace/OpportunityCard';
import {
  MarketplaceToolbar,
  EmptyState,
  RETURN_BANDS,
  DURATION_BANDS,
  FUNDING_STATES,
} from '../components/marketplace/MarketplaceToolbar';

const EMPTY_FILTERS = { strategy: '', location: '', returnBand: '', durationBand: '', fundingStatus: '' };

const LIFECYCLE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'exited', label: 'Exited' },
  { value: 'coming_soon', label: 'Coming soon' },
];

const inReturnBand = (value, band) => {
  if (!band) return true;
  const v = Number(value) || 0;
  if (band === 'lt15') return v < 15;
  if (band === '15to25') return v >= 15 && v <= 25;
  return v > 25;
};

const inDurationBand = (value, band) => {
  if (!band) return true;
  const v = Number(value) || 0;
  if (band === 'lte12') return v > 0 && v <= 12;
  if (band === '12to24') return v > 12 && v <= 24;
  return v > 24;
};

const sortOpportunities = (list, sort) => {
  const copy = [...list];
  const num = (v) => (v === null || v === undefined ? null : Number(v));
  switch (sort) {
    case 'return_desc':
      return copy.sort((a, b) => (num(b.projected_return) ?? -1) - (num(a.projected_return) ?? -1));
    case 'hold_asc':
      return copy.sort((a, b) => (num(a.target_hold_months) ?? Infinity) - (num(b.target_hold_months) ?? Infinity));
    case 'minimum_asc':
      return copy.sort((a, b) => (num(a.min_investment) ?? Infinity) - (num(b.min_investment) ?? Infinity));
    case 'funded_desc':
      return copy.sort((a, b) => (num(b.funding_progress) ?? 0) - (num(a.funding_progress) ?? 0));
    default:
      return copy.sort(
        (a, b) => new Date(b.published_at || b.created_at || 0) - new Date(a.published_at || a.created_at || 0)
      );
  }
};

const TrackRecordSummary = ({ projects, lifecycle }) => {
  const exited = projects.filter((p) => (p.lifecycle || '') === 'exited' && p.performance);
  if (lifecycle !== 'exited' || exited.length < 3) return null;

  const avg = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
  const returns = exited.map((p) => p.performance.actual_return_pct).filter((v) => v != null);
  const holds = exited.map((p) => p.performance.actual_hold_months).filter((v) => v != null);
  const capital = exited.map((p) => p.performance.capital_returned).filter((v) => v != null);
  const metProjection = exited.filter(
    (p) => p.performance.variance_pp != null && p.performance.variance_pp >= 0
  ).length;

  const stats = [
    { value: exited.length, label: 'Projects exited' },
    capital.length
      ? { value: `€${(capital.reduce((a, b) => a + b, 0) / 1000000).toFixed(1)}M`, label: 'Capital returned' }
      : null,
    returns.length ? { value: `${avg(returns).toFixed(1)}%`, label: 'Average actual return' } : null,
    holds.length ? { value: `${Math.round(avg(holds))} mo`, label: 'Average actual hold' } : null,
    returns.length
      ? { value: `${Math.round((metProjection / exited.length) * 100)}%`, label: 'Met or exceeded projection' }
      : null,
  ].filter(Boolean);

  return (
    <section className="mt-14 md:mt-16 p-6 md:p-8 rounded-3xl bg-white border border-black/[0.05]" data-testid="track-record-summary">
      <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-muted mb-5">Flippex track record</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-y-6 gap-x-8">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-2xl font-heading font-semibold tracking-tight">{s.value}</p>
            <p className="text-xs text-foreground-muted mt-1">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const SCROLL_KEY = 'flippex:explore:scroll';

const ProjectsPage = ({ context = 'public' }) => {
  const isApp = context === 'app';
  const basePath = isApp ? '/app/explore' : '/projects';
  const navigationType = useNavigationType();
  const scrollRef = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [lifecycle, setLifecycle] = useState(searchParams.get('view') || 'all');
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    strategy: searchParams.get('strategy') || '',
    location: searchParams.get('location') || '',
  });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // The brand loader needs a beat to read; hold it for a second even on a fast response.
        const [response] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects`),
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);
        setProjects(Array.isArray(response.data) ? response.data : response.data.projects || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      scrollRef.current = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      try {
        sessionStorage.setItem(SCROLL_KEY, String(scrollRef.current));
      } catch (e) {
        /* storage unavailable */
      }
    };
  }, []);

  // Returning from an opportunity keeps the results exactly where they were.
  useEffect(() => {
    if (loading || navigationType !== 'POP') return;
    const saved = Number(sessionStorage.getItem(SCROLL_KEY) || 0);
    if (saved > 0) requestAnimationFrame(() => window.scrollTo(0, saved));
  }, [loading, navigationType]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (sort !== 'newest') params.set('sort', sort);
    if (filters.strategy) params.set('strategy', filters.strategy);
    if (filters.location) params.set('location', filters.location);
    if (lifecycle !== 'all') params.set('view', lifecycle);
    setSearchParams(params, { replace: true });
  }, [search, sort, filters.strategy, filters.location, lifecycle, setSearchParams]);

  const strategyOptions = useMemo(() => {
    const seen = new Map();
    projects.forEach((p) => {
      if (p.project_type && !seen.has(p.project_type)) seen.set(p.project_type, p.project_type_label);
    });
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [projects]);

  const locationOptions = useMemo(() => {
    const seen = new Set();
    projects.forEach((p) => p.location && seen.add(p.location));
    return [...seen].map((value) => ({ value, label: value }));
  }, [projects]);

  const lifecycleOf = (p) => p.lifecycle || (p.public_status === 'closed' ? 'exited' : 'open');

  const availableTabs = useMemo(() => {
    const present = new Set(projects.map(lifecycleOf));
    return LIFECYCLE_TABS.filter((t) => t.value === 'all' || t.value === 'open' || present.has(t.value));
  }, [projects]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = projects.filter((p) => {
      if (lifecycle !== 'all' && lifecycleOf(p) !== lifecycle) return false;
      if (term && !`${p.title} ${p.location} ${p.tagline || ''}`.toLowerCase().includes(term)) return false;
      if (filters.strategy && p.project_type !== filters.strategy) return false;
      if (filters.location && p.location !== filters.location) return false;
      if (filters.fundingStatus && p.public_status !== filters.fundingStatus) return false;
      if (!inReturnBand(p.projected_return, filters.returnBand)) return false;
      if (!inDurationBand(p.target_hold_months, filters.durationBand)) return false;
      return true;
    });
    return sortOpportunities(filtered, sort);
  }, [projects, search, filters, sort, lifecycle]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch('');
    setLifecycle('all');
  };

  const activeChips = useMemo(() => {
    const label = (options, value) => options.find((o) => o.value === value)?.label || value;
    const chips = [];
    if (filters.strategy) chips.push({ key: 'strategy', label: label(strategyOptions, filters.strategy), onRemove: () => setFilter('strategy', '') });
    if (filters.location) chips.push({ key: 'location', label: filters.location, onRemove: () => setFilter('location', '') });
    if (filters.returnBand) chips.push({ key: 'return', label: label(RETURN_BANDS, filters.returnBand), onRemove: () => setFilter('returnBand', '') });
    if (filters.durationBand) chips.push({ key: 'duration', label: label(DURATION_BANDS, filters.durationBand), onRemove: () => setFilter('durationBand', '') });
    if (filters.fundingStatus) chips.push({ key: 'funding', label: label(FUNDING_STATES, filters.fundingStatus), onRemove: () => setFilter('fundingStatus', '') });
    return chips;
  }, [filters, strategyOptions]);

  return (
    <div className={isApp ? '' : 'min-h-screen bg-[rgb(var(--background))]'} data-testid="projects-page">
      {!isApp && <Header />}

      <div className="pt-8 pb-16 md:pt-14 md:pb-24">
        <div className="mx-auto w-full max-w-[1280px] px-6 md:px-10">
          {/* Hero */}
          <header className="mb-6 md:mb-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-muted">
              {isApp ? 'My Flippex' : 'Projects'}
            </p>
            <h1 className="text-3xl md:text-5xl font-heading font-semibold tracking-tight mt-2 md:mt-3">
              {isApp ? 'Explore' : 'Find something worth owning.'}
            </h1>
            <p className="text-sm md:text-base text-foreground-muted mt-2 md:mt-3">
              {isApp ? 'Find your next investment.' : 'Explore curated real estate investment opportunities.'}
            </p>
          </header>

          {/* Sticky filter bar - stays visible while the grid scrolls under it */}
          <div
            className="sticky top-16 z-30 -mx-6 md:-mx-10 px-6 md:px-10 pt-4 pb-4 bg-[rgb(var(--background))] border-b border-black/[0.05]"
            data-testid="marketplace-sticky-filters"
          >
            {/* Lifecycle selector */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide" data-testid="lifecycle-selector">
              {availableTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setLifecycle(tab.value)}
                  className={`px-3.5 h-[29px] rounded-full text-[13px] whitespace-nowrap transition-colors ${
                    lifecycle === tab.value
                      ? 'bg-[rgb(var(--foreground))] text-white'
                      : 'bg-white border border-black/[0.08] text-foreground-muted hover:border-black/20'
                  }`}
                  data-testid={`lifecycle-tab-${tab.value}`}
                >
                  {tab.label}
                </button>
              ))}
              {!loading && visible.length > 0 && (
                <p
                  className="ml-1 text-xs text-foreground-muted whitespace-nowrap"
                  data-testid="results-count"
                >
                  Showing {visible.length} {visible.length === 1 ? 'opportunity' : 'opportunities'}
                </p>
              )}
            </div>

            {/* Toolbar */}
            <MarketplaceToolbar
              search={search}
              onSearch={setSearch}
              filters={filters}
              setFilter={setFilter}
              clearFilters={clearFilters}
              sort={sort}
              onSort={setSort}
              strategyOptions={strategyOptions}
              locationOptions={locationOptions}
              resultCount={visible.length}
              activeChips={activeChips}
            />

          </div>

          {/* Grid */}
          {loading ? (
            <div className="py-24 flex justify-center" data-testid="projects-loading">
              <FlippexLoader size={48} />
            </div>
          ) : visible.length === 0 ? (
            <EmptyState onClear={clearFilters} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6 mt-4 md:mt-5" data-testid="opportunity-grid">
              {visible.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: Math.min(index * 0.06, 0.3) }}
                >
                  <OpportunityCard project={project} basePath={basePath} />
                </motion.div>
              ))}
            </div>
          )}

          {/* Track record summary — only when enough real exited data exists */}
          <TrackRecordSummary projects={projects} lifecycle={lifecycle} />

          {/* Trust strip */}
          {!loading && visible.length > 0 && (
            <div
              className="mt-14 md:mt-20 pt-6 border-t border-black/[0.06] flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
              data-testid="trust-strip"
            >
              <p className="text-xs text-foreground-muted">
                Curated projects · Asset-backed structure · Independent verification
              </p>
              <a
                href="/how-it-works"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:gap-2.5 transition-all sm:ml-auto"
              >
                Learn how Flippex works <ArrowRight size={13} weight="bold" />
              </a>
            </div>
          )}
        </div>
      </div>

      {!isApp && <Footer />}
    </div>
  );
};

export default ProjectsPage;
